import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  QeukHeroAttribution,
  QeukTrustNudge,
  QeukPaymentPlaceholder,
  QeukFooter,
} from './QeukAttribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 8-image gallery config — shared between SL landing and /tn.
export const GALLERY = [
  { src: '/marketing/hero-01-boy-among-stars.jpeg',                key: 'gallery.01', fallback: 'Dream big under one sky' },
  { src: '/marketing/hero-02-boy-galaxy-window.jpeg',              key: 'gallery.02', fallback: 'Every child has a universe to explore' },
  { src: '/marketing/hero-03-ai-hand-touch.jpeg',                  key: 'gallery.03', fallback: 'AI is a tool. Kids are the makers.' },
  { src: '/marketing/hero-04-two-boys-friendship.jpeg',            key: 'gallery.04', fallback: 'Learn together, grow together' },
  { src: '/marketing/hero-05-girl-engineer-watercolor.jpeg',       key: 'gallery.05', fallback: 'She built it. So can you.' },
  { src: '/marketing/hero-06-grandmother-granddaughter-watercolor.jpeg', key: 'gallery.06', fallback: 'For every amma. For every aachchi.' },
  { src: '/marketing/hero-07-tamil-family-watercolor.jpeg',        key: 'gallery.07', fallback: 'Family is the first classroom' },
  { src: '/marketing/hero-08-ai-dial-watercolor.jpeg',             key: 'gallery.08', fallback: "Tomorrow's tools, today's kids" },
  { src: '/marketing/hero-09-grandmother-granddaughter-bench.jpeg', key: 'gallery.09', fallback: 'Amma knows best. Amma helps.' },
  { src: '/marketing/hero-10-family-certificate.jpeg',             key: 'gallery.10', fallback: 'Every family. Every certificate. Every celebration.' },
  { src: '/marketing/hero-11-kid-pointing-laptop.jpeg',            key: 'gallery.11', fallback: 'My child just built something.' },
];

export const MarketingGallery = () => {
  const { t } = useTranslation();
  return (
    <section className="mt-20" data-testid="marketing-gallery">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
        {t('gallery.section.title') || 'Our vision, painted'}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {GALLERY.map((g, i) => (
          <div key={g.src} className="group relative overflow-hidden rounded-2xl shadow-md"
               data-testid={`gallery-item-${i + 1}`}>
            <img src={g.src} alt="" loading="lazy"
                 className="w-full h-64 object-cover transition duration-500 group-hover:scale-105" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4">
              <p className="text-white text-sm italic">
                {t(g.key) || g.fallback}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// --------------------------------------------------------------------------- //
// Tamil Nadu preview landing
// --------------------------------------------------------------------------- //
export default function LandingTN() {
  const { t, i18n } = useTranslation();
  const [wa, setWa] = useState('');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    // Auto-switch to Tamil on first arrival if the user is currently in EN
    if (['en'].includes(i18n.language)) i18n.changeLanguage('ta');
  }, [i18n]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const digits = wa.replace(/[^\d]/g, '');
      const number = digits.startsWith('91') ? `+${digits}` : `+91${digits}`;
      await axios.post(`${API}/waitlist/tn`, { whatsapp: number, source: 'landing_tn' });
      setStatus('done');
    } catch (e) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <nav className="flex justify-between items-center mb-8">
          <div>
            <p className="text-xs text-muted-foreground uppercase">Genius One Plus</p>
            <p className="text-lg font-bold">தமிழ்நாடு</p>
          </div>
          <Link to="/" className="text-sm underline">← Back to Sri Lanka site</Link>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-orange-200 text-orange-900 text-xs font-semibold mb-4">
              {t('waitlist.eta.coming_2027') || 'Coming 2027'}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('tn.hero.title') || 'Genius One Plus is coming to Tamil Nadu'}
            </h1>
            <QeukHeroAttribution testidPrefix="tn" />
            <p className="text-lg text-muted-foreground mb-6">
              {t('tn.hero.sub') || 'Chennai • Coimbatore • Madurai — Genius One Plus is coming'}
            </p>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t('tn.waitlist.title') || 'Notify me when we launch in Tamil Nadu'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {status === 'done' ? (
                  <div className="text-emerald-700 text-sm" data-testid="tn-waitlist-thanks">
                    ✓ {t('tn.waitlist.done') || 'You are on the list. We will WhatsApp you when we launch.'}
                  </div>
                ) : (
                  <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
                    <Input placeholder="+91 98765 43210" value={wa} onChange={(e) => setWa(e.target.value)}
                           data-testid="tn-waitlist-input" required />
                    <Button type="submit" disabled={status === 'loading'} data-testid="tn-waitlist-submit">
                      {status === 'loading' ? 'Sending…' : (t('tn.waitlist.cta') || 'Notify me')}
                    </Button>
                  </form>
                )}
                {status === 'error' && (
                  <p className="text-xs text-red-600 mt-2">Please enter a valid Indian WhatsApp number.</p>
                )}
                <p className="text-xs text-muted-foreground mt-3">
                  {t('tn.pricing.tba') || 'INR pricing to be announced closer to launch. No purchases yet.'}
                </p>
                <QeukTrustNudge testidPrefix="tn" />
              </CardContent>
            </Card>
            <QeukPaymentPlaceholder testidPrefix="tn" />
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-orange-200/40 rounded-3xl rotate-2" />
            <img src="/marketing/hero-07-tamil-family-watercolor.jpeg"
                 alt="Tamil family learning together"
                 className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
                 data-testid="tn-hero-img" />
          </div>
        </div>

        <MarketingGallery />

        <div className="mt-10 text-center">
          <QeukFooter testidPrefix="tn" />
        </div>
      </div>
    </div>
  );
}
